// OIDC Authorization Code + PKCE flow for Keycloak

import { brand, BRAND_DEFAULTS, brandLocales, brandDefaultLocale } from '@parsina/framework/ui/lib/brand';
import { KC_BASE_URL, kcRealm, kcClientId, kcBffClientId } from './runtimeEnv.js';

// The realm's URL, resolved on every call like the client id (kcClientId()):
// both come from the deployment, and runtimeEnv.js says in which order.
const kcBase = () => `${KC_BASE_URL}/realms/${encodeURIComponent(kcRealm())}`;

// Always derived at call time so redirect_uri is consistent across
// the auth request and token exchange even if the page reloads.
function getRedirectUri() {
  return `${window.location.origin}/`;
}

// ── Login language ───────────────────────────────────────────────────────────
// Tell Keycloak which language to serve the sign-in in, so the login page AND
// the errors it resolves server-side (bad password, locked account, expired
// code) come back in the language this panel is running in rather than the
// realm's own default.
//
// Read from `<html lang>`, which I18nProvider owns, so it follows the panel
// without this file knowing anything about the locale registry.
//
// `ui_locales` is the OIDC standard hint; `kc_locale` is what Keycloak acts on,
// and it sets the KEYCLOAK_LOCALE cookie so the choice survives every later
// step of the flow rather than just the first page.
//
// ── why this list is the THEME'S, not the panel's ─────────────────────────────
//
// Deliberately not the panel's locale list but the sign-in theme's: the
// languages the RELEASED sso/parsina.keycloakify carries message bundles for
// (IMPROVED_LANGUAGE_TAGS — fa, en, ar). Sending `kc_locale` for a language the
// theme lacks gets a login page half in Keycloak's own fallback, which is worse
// than a coherent one, so a language joins this list only once the theme that
// carries it is released and deployed (docs/I18N.md «Adding a language» step 8).
// (Until PM-0185 this said the theme shipped fa and en only; it had shipped `ar`
// for a while, so an Arabic panel signed in through the Persian page for nothing.)
//
// French is NOT here yet (PM-0185, T71): the theme with an `fr` bundle exists
// only on sso's feat/fr-locale and is unreleased. 'fr' is added to this list in
// the release that follows the theme's deployment, never before. Until then a
// French panel signs in through the ENGLISH page — LOGIN_NEAREST below — since
// missing French falls back to English, never to Persian (FRENCH.md D4).
//
// German is NOT here either (PM-0212, T71): the sign-in theme's `de` bundle is
// on sso's feat/de-locale only and unreleased, so 'de' joins this list in the
// release after that theme is deployed, never before. Until then a German panel
// signs in through the ENGLISH page as well (LOGIN_NEAREST) — missing German
// falls back to English, never to Persian.
//
// A BRAND narrows this to the languages it offers and moves the fallback to its
// own first-visit language (@parsina/framework/ui/lib/brand): a brand that does
// not offer Persian never sends `kc_locale=fa`. A brand of its own also signs
// in through a theme of its own — Proxia's (proxia.keycloakify, realm locales
// en + fa) carries no Arabic, French or German — so it keeps the two languages every
// theme carries, and its Arabic panel signs in through its default (English).
// With no brand this is the list below and Persian the fallback.
const LOGIN_LOCALES = ['fa', 'en', 'ar'];
const ANY_THEME_LOCALES = ['fa', 'en'];
// A panel language the theme lacks signs in through its nearest one the theme
// has, before the brand's fallback: French and German read English (D4).
const LOGIN_NEAREST = { fr: 'en', de: 'en' };

function panelLocale() {
  const lang = (document.documentElement.getAttribute('lang') || '')
    .toLowerCase()
    .split('-')[0];
  const theme = brand().id === BRAND_DEFAULTS.id ? LOGIN_LOCALES : ANY_THEME_LOCALES;
  const offered = brandLocales(theme);
  if (offered.includes(lang)) return lang;
  const nearest = LOGIN_NEAREST[lang];
  return nearest && offered.includes(nearest) ? nearest : brandDefaultLocale('fa', theme);
}

// ── PKCE helpers ─────────────────────────────────────────────────────────────

function randomBytes(len) {
  const arr = new Uint8Array(len);
  crypto.getRandomValues(arr);
  return arr;
}

function base64url(buf) {
  return btoa(String.fromCharCode(...buf))
    .replace(/\+/g, '-').replace(/\//g, '_').replace(/=/g, '');
}

async function sha256(str) {
  const enc = new TextEncoder().encode(str);
  const buf = await crypto.subtle.digest('SHA-256', enc);
  return new Uint8Array(buf);
}

async function generateChallenge(verifier) {
  return base64url(await sha256(verifier));
}

// ── Token storage ─────────────────────────────────────────────────────────────

// A sign-in lasts as long as Keycloak's refresh token — 30 days on beta1 — not
// as long as one tab (PM-0158). The REFRESH token and the id token (the logout
// hint) live in localStorage, so a new tab, a reopened browser or a Chrome that
// Android killed in the background signs in silently from them. The 5-minute
// ACCESS token stays per tab. Before this, all three lived in sessionStorage and
// every new tab or browser restart threw a valid 30-day token away and sent the
// user to the login form. A copy left in sessionStorage by an older build is
// still read, so a tab open across the deploy keeps working.
const PERSISTED = ['kc_refresh', 'kc_id_token'];
function durable() {
  try { return typeof window !== 'undefined' ? window.localStorage : null; } catch { return null; }
}
function readPersisted(key) {
  try {
    const v = durable()?.getItem(key);
    if (v) return v;
  } catch { /* storage blocked: fall through to the tab's copy */ }
  return sessionStorage.getItem(key);
}

export function getToken()   { return sessionStorage.getItem('kc_token'); }
export function getRefresh() { return readPersisted('kc_refresh'); }
export function getIdToken() { return readPersisted('kc_id_token'); }

function saveTokens(token, refresh, idToken) {
  sessionStorage.setItem('kc_token', token);
  const pairs = [['kc_refresh', refresh], ['kc_id_token', idToken]].filter(([, v]) => v);
  try {
    const store = durable();
    if (!store) throw new Error('no localStorage');
    pairs.forEach(([k, v]) => store.setItem(k, v));
    pairs.forEach(([k]) => sessionStorage.removeItem(k));
  } catch {
    // Private mode or blocked storage: the tab still works, as it always did.
    pairs.forEach(([k, v]) => sessionStorage.setItem(k, v));
  }
}

export function clearTokens() {
  ['kc_token', 'kc_refresh', 'kc_id_token', 'kc_verifier', 'kc_state'].forEach(k =>
    sessionStorage.removeItem(k)
  );
  try { PERSISTED.forEach(k => durable()?.removeItem(k)); } catch { /* nothing stored */ }
}

// Keycloak ROTATES the refresh token. With it shared across tabs, two tabs
// refreshing at the same moment would present the same token twice and the
// second would be refused — and a refused refresh signs the user out. The
// browser's Web Locks serialise refreshes across every tab of this origin; each
// refresh reads the stored token inside the lock, so it uses the one the
// previous tab just received. Where Web Locks are missing, the per-tab
// single-flight below still holds.
async function withRefreshLock(fn) {
  const locks = typeof navigator !== 'undefined' ? navigator.locks : null;
  if (!locks || typeof locks.request !== 'function') return fn();
  return locks.request('parsina-kc-refresh', () => fn());
}

// ── Redirect-loop guard ───────────────────────────────────────────────────────
// Records each redirect timestamp. If 3+ happen within 30 s, stops redirecting
// and lets the app render an error screen instead.

const LOOP_KEY   = 'kc_redirect_ts';
const LOOP_LIMIT = 3;
const LOOP_WIN   = 30_000; // ms

function redirectAllowed() {
  const raw    = sessionStorage.getItem(LOOP_KEY);
  const hist   = raw ? JSON.parse(raw) : [];
  const now    = Date.now();
  const recent = hist.filter(ts => now - ts < LOOP_WIN);
  if (recent.length >= LOOP_LIMIT) return false;
  sessionStorage.setItem(LOOP_KEY, JSON.stringify([...recent, now]));
  return true;
}

export function clearRedirectGuard() {
  sessionStorage.removeItem(LOOP_KEY);
}

// ── Where to come back to after signing in ───────────────────────────────────
//
// `redirect_uri` is pinned to the origin and MUST stay pinned — it is registered
// on the Keycloak client and a per-path value would have to be registered too. So
// the path is remembered on this side instead.
//
// Without this, every deep link dies at the login screen: the person arrives at
// `/c/mehr/payments`, signs in, and lands on `/`. The link that costs the most is
// `/invite/:code` from a printed QR code — the person scans it, authenticates, and
// ends up on a start page with no idea the code went nowhere.
//
// Only a same-origin PATH is ever honoured. It is stored by this app, but it is
// read back after a round trip through an external identity provider, so it is
// validated as if it were hostile: anything with a scheme or a host is discarded,
// which is what stops a crafted value turning the login flow into an open redirect.

const RETURN_KEY = 'auth.return.v1';

/** Paths that must never be returned to — they are where you land WITHOUT one. */
const NO_RETURN = ['/start', '/onboarding'];

export function stashReturnPath(path) {
  try {
    const raw = String(path || '');
    if (!raw.startsWith('/') || raw.startsWith('//')) return;
    if (NO_RETURN.some((p) => raw === p || raw.startsWith(`${p}?`))) return;
    if (raw === '/' || raw.startsWith('/?')) return;
    sessionStorage.setItem(RETURN_KEY, raw);
  } catch { /* a lost return path costs one navigation, never the sign-in */ }
}

export function takeReturnPath() {
  try {
    const raw = sessionStorage.getItem(RETURN_KEY);
    sessionStorage.removeItem(RETURN_KEY);
    if (!raw || !raw.startsWith('/') || raw.startsWith('//')) return '';
    return raw;
  } catch {
    return '';
  }
}

// ── Auth flow ─────────────────────────────────────────────────────────────────

export async function redirectToLogin() {
  // Before anything else: the current location is about to be replaced by
  // Keycloak's, and this is the last moment it exists.
  stashReturnPath(window.location.pathname + window.location.search);

  if (!redirectAllowed()) {
    // Too many consecutive redirects — surface the error to the user instead.
    clearTokens();
    window.history.replaceState({}, '', '/?auth_error=loop');
    window.location.reload();
    return;
  }

  const verifier  = base64url(randomBytes(32));
  const challenge = await generateChallenge(verifier);
  const state     = base64url(randomBytes(16));

  sessionStorage.setItem('kc_verifier', verifier);
  sessionStorage.setItem('kc_state',    state);

  const params = new URLSearchParams({
    response_type:         'code',
    client_id:             kcClientId(),
    redirect_uri:          getRedirectUri(),
    scope:                 'openid profile email',
    state,
    code_challenge:        challenge,
    code_challenge_method: 'S256',
    ui_locales:            panelLocale(),
    kc_locale:             panelLocale(),
  });

  window.location.href = `${kcBase()}/protocol/openid-connect/auth?${params}`;
}

// Silent re-auth after role change (e.g. post-onboarding).
// Uses prompt=none — KC redirects back immediately using the active SSO session,
// issuing a fresh token that includes newly assigned client roles.
export async function reauthorize() {
  console.log('[reauthorize] starting silent KC re-auth with prompt=none');
  const verifier  = base64url(randomBytes(32));
  const challenge = await generateChallenge(verifier);
  const state     = base64url(randomBytes(16));

  sessionStorage.setItem('kc_verifier', verifier);
  sessionStorage.setItem('kc_state',    state);

  const params = new URLSearchParams({
    response_type:         'code',
    client_id:             kcClientId(),
    redirect_uri:          getRedirectUri(),
    scope:                 'openid profile email',
    state,
    code_challenge:        challenge,
    code_challenge_method: 'S256',
    prompt:                'none',
    ui_locales:            panelLocale(),
    kc_locale:             panelLocale(),
  });

  const url = `${kcBase()}/protocol/openid-connect/auth?${params}`;
  console.log('[reauthorize] redirecting to KC:', url);
  window.location.href = url;
}

export async function handleCallback() {
  const params   = new URLSearchParams(window.location.search);
  const error    = params.get('error');
  const code     = params.get('code');
  const state    = params.get('state');
  const verifier = sessionStorage.getItem('kc_verifier');
  const saved    = sessionStorage.getItem('kc_state');

  console.log('[handleCallback] error:', error, '| hasCode:', !!code, '| stateMatch:', state === saved, '| hasVerifier:', !!verifier);

  // Keycloak returned an explicit error (access_denied, login_required, etc.)
  if (error) {
    console.warn('[handleCallback] KC returned error:', error, params.get('error_description'));
    clearTokens();
    window.history.replaceState({}, '', '/');
    return false;
  }

  // Missing or mismatched PKCE / state — don't retry immediately
  if (!code || !verifier || state !== saved) {
    console.warn('[handleCallback] missing code/verifier or state mismatch — aborting');
    window.history.replaceState({}, '', '/');
    return false;
  }

  const res = await fetch(`${kcBase()}/protocol/openid-connect/token`, {
    method:  'POST',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    body: new URLSearchParams({
      grant_type:    'authorization_code',
      client_id:     kcClientId(),
      redirect_uri:  getRedirectUri(),
      code,
      code_verifier: verifier,
    }),
  });

  // Strip the OAuth query and restore the path the person actually asked for.
  // On failure this still lands on '/', because a return path is only meaningful
  // once there is a session to view it with.
  const returnTo = takeReturnPath();
  window.history.replaceState({}, '', res.ok ? (returnTo || '/') : '/');

  if (!res.ok) {
    console.error('[handleCallback] token exchange failed:', res.status, await res.text());
    return false;
  }

  const data = await res.json();
  if (!data.access_token) {
    console.error('[handleCallback] no access_token in response:', JSON.stringify(data));
    return false;
  }

  // Log roles from new token
  try {
    const claims = JSON.parse(atob(data.access_token.split('.')[1].replace(/-/g,'+').replace(/_/g,'/')));
    const roles = claims?.resource_access?.[kcBffClientId()]?.roles || [];
    console.log('[handleCallback] new token roles:', roles, '| sub:', claims?.sub, '| clinic_id:', claims?.clinic_id);
  } catch { /* non-fatal */ }

  saveTokens(data.access_token, data.refresh_token, data.id_token);
  clearRedirectGuard();
  console.log('[handleCallback] tokens saved successfully');
  return true;
}

/**
 * A usable access token, refreshing first when the current one is stale; null
 * when there is none to be had. Shared by this panel's transport and the
 * clinical module's (lib/clinicalHost.js), so both renew the session the same way.
 */
export async function getValidToken() {
  let token = getToken();
  if (!token) return null;
  if (isTokenExpired(token)) {
    const ok = await refreshAccessToken();
    if (!ok) return null;
    token = getToken();
  }
  return token;
}

async function refreshAccessTokenInTab() {
  const refresh = getRefresh();
  if (!refresh) return false;

  const res = await fetch(`${kcBase()}/protocol/openid-connect/token`, {
    method:  'POST',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    body: new URLSearchParams({
      grant_type:    'refresh_token',
      client_id:     kcClientId(),
      refresh_token: refresh,
    }),
  });

  if (!res.ok) { clearTokens(); return false; }
  const data = await res.json();
  if (!data.access_token) { clearTokens(); return false; }
  saveTokens(data.access_token, data.refresh_token, data.id_token);
  return true;
}

/**
 * The BFF client's roles in an access token — the current one by default. That
 * client is `parsina-bff` unless the deployment names another (kcBffClientId).
 */
export function tokenRoles(token = getToken()) {
  return parseJwt(token || '')?.resource_access?.[kcBffClientId()]?.roles || [];
}

const pause = (ms) => new Promise((resolve) => { setTimeout(resolve, ms); });

/**
 * Refresh the access token until it carries `role`, a few times at most.
 *
 * Keycloak writes a role into a token only when it MINTS one, so a role granted
 * a moment after the last refresh is invisible until the next — and the grants
 * this panel waits on are written by another service, some of them
 * fire-and-forget (`assignDoctorRole` in `completeOnboardingStep1`). One refresh
 * right after the call that asked for the grant can therefore come back a token
 * too early; a short retry is the difference between the panel adopting the role
 * now and the person having to reload to see it.
 *
 * Bounded on purpose, and never a failure: a grant that never lands must not
 * hold anyone on a spinner. The caller carries on either way — the answer only
 * says whether the token it now holds has the role. A failed refresh stops at
 * once, because `refreshAccessToken` has already dropped the session and another
 * try cannot bring it back.
 *
 * @returns {Promise<boolean>} whether the current token carries `role`
 */
export async function refreshUntilRole(role, { attempts = 4, delayMs = 750, wait = pause } = {}) {
  if (tokenRoles().includes(role)) return true;
  for (let i = 0; i < attempts; i += 1) {
    if (i > 0) await wait(delayMs);
    let ok = false;
    try { ok = await refreshAccessToken(); } catch { ok = false; }
    if (!ok) return false;
    if (tokenRoles().includes(role)) return true;
  }
  return false;
}

export function logout() {
  const idToken = getIdToken();
  clearTokens();
  clearRedirectGuard();
  const params = new URLSearchParams({
    client_id:                kcClientId(),
    post_logout_redirect_uri: getRedirectUri(),
    ui_locales:               panelLocale(),
    kc_locale:                panelLocale(),
  });
  if (idToken) params.set('id_token_hint', idToken);
  window.location.href = `${kcBase()}/protocol/openid-connect/logout?${params}`;
}

export function parseJwt(token) {
  try {
    const b64 = token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
    const bytes = Uint8Array.from(atob(b64), c => c.charCodeAt(0));
    return JSON.parse(new TextDecoder().decode(bytes));
  } catch {
    return null;
  }
}

export function isTokenExpired(token) {
  const p = parseJwt(token);
  if (!p) return true;
  return Date.now() >= p.exp * 1000 - 30_000; // 30 s buffer
}

// The exported refresh: one per tab (single-flight above), one at a time across
// tabs (Web Lock), so a rotated refresh token is never presented twice (PM-0158).
export function refreshAccessToken() {
  return withRefreshLock(() => refreshAccessTokenInTab());
}
